import { resolveLanguage, type AskRequest, type UiLanguage } from "@ai-guidance/shared";

/**
 * Cele trei triggere, derivate din contractul partajat (`askRequestSchema.nudge`)
 * ca să nu existe două liste care pot diverge: ce nu poate fi trimis pe /ask nu
 * are voie nici să declanșeze un balon.
 */
export type NudgeTrigger = NonNullable<AskRequest["nudge"]>;

/**
 * Copy-ul nudge-urilor proactive (docs/plan-commerce-proactivity.md → tabelul
 * de triggere). TEMPLATE-uri, nu LLM: nudge-ul e gratis (zero tokeni, zero
 * cotă, zero halucinație) și instant. Modelul intră în joc abia dacă
 * vizitatorul răspunde.
 *
 * Două limbi, alese după `business.language` din configul appului (fallback
 * engleză) - vezi `nudgeLanguage`. Nu e i18n real (widgetul nu are așa ceva,
 * vezi commerce/text.ts): sunt exact cele două limbi în care avem magazine.
 * Editabile de merchant abia într-o fază ulterioară - v1 le hardcodează.
 *
 * Variabilele ({n}, {total}) vin din starea REALĂ a coșului, citită de
 * CommerceRuntime din magazin - aceeași disciplină anti-halucinație ca la
 * carduri: nicio cifră din nudge nu e compusă de noi.
 */

export type NudgeLanguage = UiLanguage;

/**
 * Forma de singular și cea de plural sunt texte separate, nu un „{n} produse"
 * cu n=1 lipit peste: „Ai 1 produse în coș" e prima propoziție pe care o
 * citește vizitatorul de la asistent, iar un acord greșit acolo îl face să
 * arate exact ca un pop-up generat automat - adică fix lucrul de care ne
 * despărțim prin design.
 */
interface NudgeCopy {
  hesitation: string;
  staleCartOne: string;
  staleCartMany: string;
  exitIntent: string;
  /** `aria-label`-ul lui ✕ - urmează limba nudge-ului, nu engleza restului panoului: e singurul text din jurul lui. */
  dismiss: string;
}

const COPY: Record<NudgeLanguage, NudgeCopy> = {
  ro: {
    hesitation: "Ai o întrebare despre produsul ăsta? Te pot ajuta să compari.",
    staleCartOne: "Ai un produs în coș ({total}). Vrei să finalizăm sau mai cauți ceva?",
    staleCartMany: "Ai {n} produse în coș ({total}). Vrei să finalizăm sau mai cauți ceva?",
    exitIntent: "Înainte să pleci - vrei să-ți păstrez coșul sau ai o nedumerire la care pot răspunde?",
    dismiss: "Închide",
  },
  en: {
    hesitation: "Have a question about this product? I can help you compare.",
    staleCartOne: "You have one item in your cart ({total}). Shall we check out, or are you still looking?",
    staleCartMany: "You have {n} items in your cart ({total}). Shall we check out, or are you still looking?",
    exitIntent: "Before you go - shall I keep your cart, or is there something I can clear up?",
    dismiss: "Dismiss",
  },
};

/**
 * Alegerea limbii nu se face aici: e `resolveLanguage` din `shared`, folosit
 * identic de chrome-ul widgetului și de backend. Numele vechi rămâne ca alias,
 * fiindcă e cel sub care îl cheamă motorul de nudge.
 */
export const nudgeLanguage = resolveLanguage;

/** Coșul, atât cât îi trebuie textului: câte produse și cât face. */
export interface NudgeCartFacts {
  itemCount: number;
  totalPrice: string;
}

/**
 * Textul afișat pentru un trigger. `cart` e obligatoriu doar acolo unde
 * template-ul chiar are variabile (stale_cart); pentru celelalte două e ignorat,
 * deci un coș necunoscut nu poate produce un text cu găuri în el.
 */
export function nudgeText(trigger: NudgeTrigger, lang: NudgeLanguage, cart?: NudgeCartFacts): string {
  const copy = COPY[lang];
  if (trigger === "hesitation") return copy.hesitation;
  if (trigger === "exit_intent") return copy.exitIntent;
  const count = cart?.itemCount ?? 0;
  const total = cart?.totalPrice ?? "";
  const template = count === 1 ? copy.staleCartOne : copy.staleCartMany;
  return template.replace("{n}", String(count)).replace("{total}", total);
}

/** Eticheta accesibilă a butonului ✕, în aceeași limbă ca nudge-ul. */
export function nudgeDismissLabel(lang: NudgeLanguage): string {
  return COPY[lang].dismiss;
}
